import React, { Suspense, useEffect, useState } from "react";
import "./App.css";
import {
  Route,
  Routes,
  useLocation,
  Navigate,
} from "react-router-dom";
import { BrowserRouter as Router } from "react-router-dom";
import { User } from "./components/types/User";
import { GoogleOAuthProvider } from "@react-oauth/google";
import {
  clearAuthTokens,
  getCurrentUser,
  getIsAuthenticated,
  logout,
  setProfilePic,
  setUserRole,
  setUserEmail,
} from "./services/apiService";
import { unregisterPushDevice } from "./services/push-notification-service";
import { useUser } from "./components/login/UserContext";
import SupportWidget from "./features/support/SupportWidget";
import PostToFeedToast from "./components/feed/PostToFeedToast";
import BoardToast from "./components/products/common/board-toast";
import BillingStrip from "./components/payments/BillingStrip";
import ParlaySlipDock from "./components/products/parlay-slip/ParlaySlipDock";
import AnnouncementHost from "./components/announcements/AnnouncementHost";
import TermsGate from "./components/legal/TermsGate";
import { defaultUser } from "./components/login/defaultUser";
import SignInModal from "./components/login/SignInModal";
import { signInModalSignal } from "./components/login/signInModalSignal";
import { resumeCheckoutAfterLogin } from "./components/payments/checkoutIntent";
import { useSignalValue } from "./utils/signal";
import GoogleOneTap from "./components/login/GoogleOneTap";
import NavbarHandler from "./hooks/NavbarHandler";
import AppNav from "./components/navigation/AppNav";
import { isChromelessRoute, isImmersiveRoute } from "./components/navigation/navConfig";
import Disclaimer from "./components/landing-page/footer/disclaimer";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { NotificationProvider } from "./components/notifications";
import AgentStatusDrawer from "./components/products/common/bet-agent/AgentStatusDrawer";
import { ensureBetAgentDetected } from "./components/products/common/bet-agent/betAgent";
import { currentUserHasBetAgentAccess } from "./components/login/hasAccess";
import { hydrateUserStateFromAccount } from "./components/products/promo-finder/promo-finder";
import { capture, capturePageview, resetPostHog } from "./analytics/posthog";
import { captureGa4Pageview, resetGa4 } from "./analytics/ga4";
import { resetClarity } from "./analytics/clarity";
import { lazyWithRetry } from "./components/commons/lazyWithRetry";
import { getPricingDestination } from "./components/payments/pricing-routing";
import GlassDefs from "./components/ui/glass/GlassDefs";

const Directory = lazyWithRetry(() => import("./components/landing-page/footer/directory"));
const GlassLab = lazyWithRetry(() => import("./components/glass-lab/GlassLab"));
const ExitSurveyPage = lazyWithRetry(() => import("./components/exit-survey/ExitSurveyPage"));
const ComeBackPage = lazyWithRetry(() => import("./features/lifecycle/ComeBackPage"));
const ReactivatePage = lazyWithRetry(() => import("./features/lifecycle/ReactivatePage"));
const CustomerCallPage = lazyWithRetry(() => import("./components/customer-call/CustomerCallPage"));

/* ─── Lazy-loaded route components (code-split into separate chunks) ─── */
const ActivationPage = lazyWithRetry(() => import("./features/lifecycle/ActivationPage"));
const OddsPage = lazyWithRetry(() => import("./features/odds/OddsPage"));
const OddsGamePage = lazyWithRetry(() => import("./features/odds/OddsGamePage"));
const StartPage = lazyWithRetry(() => import("./features/start/StartPage"));
const PlanPage = lazyWithRetry(() => import("./features/start/PlanPage"));
const EmailLinkVerify = lazyWithRetry(() => import("./components/login/EmailLinkVerify"));
const RelationshipsPage = lazyWithRetry(() => import("./features/lifecycle/RelationshipsPage"));
const HomeRoute = lazyWithRetry(() => import("./components/landing-page/HomeRoute"));
const Login = lazyWithRetry(() => import("./components/login/Login"));
const FeedPage = lazyWithRetry(() => import("./components/feed/FeedPage"));
const PostThreadPage = lazyWithRetry(() => import("./components/feed/PostThreadPage"));
const CommunitiesPage = lazyWithRetry(() => import("./components/communities/CommunitiesPage"));
const CreateCommunityPage = lazyWithRetry(() => import("./components/communities/CreateCommunityPage"));
const EditCommunityPage = lazyWithRetry(() => import("./components/communities/EditCommunityPage"));
const CommunityPage = lazyWithRetry(() => import("./components/communities/CommunityPage"));
const CommunityMembersPage = lazyWithRetry(() => import("./components/communities/CommunityMembersPage"));
const TermsOfServicePage = lazyWithRetry(() => import("./components/legal/TermsOfServicePage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./components/legal/PrivacyPolicyPage"));
const AccountSettings = lazyWithRetry(() => import("./components/account/AccountSettings"));
const PublicProfile = lazyWithRetry(() => import("./components/account/PublicProfile"));
const PositiveEVRoute = lazyWithRetry(() => import("./components/products/positive-ev/positive-ev-route"));
const PerformanceRoute = lazyWithRetry(() => import("./components/products/performance/performance-route"));
const MyBetsRoute = lazyWithRetry(() => import("./components/products/my-bets/my-bets-route"));
const EVTip = lazyWithRetry(() => import("./components/products/positive-ev/positive-ev-tips"));
const PositiveEVShare = lazyWithRetry(() => import("./components/products/positive-ev/positive-ev-share"));
const NewSubscriber = lazyWithRetry(() => import("./components/products/positive-ev/components/new-subscriber/new-subscriber"));
const SharpMoneyRoute = lazyWithRetry(() => import("./components/products/sharp-money/sharp-money-route"));
const SharpMoneyShare = lazyWithRetry(() => import("./components/products/sharp-money/sharp-money-share"));
const PredictionTradersRoute = lazyWithRetry(() => import("./components/products/prediction-traders/prediction-traders-route"));
const PredictionTradersShare = lazyWithRetry(() => import("./components/products/prediction-traders/prediction-traders-share"));
const ArbitrageRoute = lazyWithRetry(() => import("./components/products/arbitrage/arbitrage-route"));
const ArbitrageShare = lazyWithRetry(() => import("./components/products/arbitrage/arbitrage-share"));
const AllPromos = lazyWithRetry(() => import("./components/products/promo-finder/promo-finder-full").then(m => ({ default: m.AllPromos })));
const ContentPostView = lazyWithRetry(() => import("./components/content/content-post/content-post-view"));
const NotFound = lazyWithRetry(() => import("./components/not-found/not-found"));
const BettingEducation = lazyWithRetry(() => import("./components/content/betting-education/betting-education"));
const BrawlerBlog = lazyWithRetry(() => import("./components/content/blog/brawler-blog"));
const BettingCalculators = lazyWithRetry(() => import("./components/content/betting-calculators/betting-calculators"));
const CalculatorContent = lazyWithRetry(() => import("./components/content/betting-calculators/calculator-content"));
const EmailUnsubscribedPage = lazyWithRetry(() => import("./components/unsubscribe/EmailUnsubscribedPage"));
const BroadcastPage = lazyWithRetry(() => import("./features/broadcast/BroadcastPage").then(m => ({ default: m.BroadcastPage })));
const ContactEmailPage = lazyWithRetry(() => import("./features/broadcast/ContactEmailPage").then(m => ({ default: m.ContactEmailPage })));
// Admin dashboard — analytics, segment-driven campaigns, user
// outreach. The shell + every nested page enforces ADMIN role on the
// front-end (defense-in-depth on top of the /api/v1/admin/dashboard/**
// security-chain rule).
const ProfitEstimatorPage = lazyWithRetry(() => import("./features/estimator/ProfitEstimatorPage"));
const FreeAccountPage = lazyWithRetry(() => import("./features/free-account/FreeAccountPage"));
const AdminLayout = lazyWithRetry(() => import("./features/admin/AdminLayout"));
const AdminOverviewPage = lazyWithRetry(() => import("./features/admin/pages/OverviewPage"));
const AdminAnalyticsPage = lazyWithRetry(() => import("./features/admin/pages/AnalyticsPage"));
const AdminInsightsPage = lazyWithRetry(() => import("./features/admin/pages/InsightsPage"));
const AdminUsersPage = lazyWithRetry(() => import("./features/admin/pages/UsersPage"));
const AdminLeadsPage = lazyWithRetry(() => import("./features/admin/pages/LeadsPage"));
const AdminRecentSignupsPage = lazyWithRetry(() => import("./features/admin/pages/RecentSignupsPage"));
const AdminUserDetailPage = lazyWithRetry(() => import("./features/admin/pages/UserDetailPage"));
const AdminSegmentsPage = lazyWithRetry(() => import("./features/admin/pages/SegmentsPage"));
const AdminEmailSettingsPage = lazyWithRetry(() => import("./features/admin/pages/EmailSettingsPage"));
const AdminCampaignsListPage = lazyWithRetry(() => import("./features/admin/pages/CampaignsListPage"));
const AdminCampaignDetailPage = lazyWithRetry(() => import("./features/admin/pages/CampaignDetailPage"));
const AdminCampaignBuilderPage = lazyWithRetry(() => import("./features/admin/pages/CampaignBuilderPage"));
const AdminGrowthFindingsPage = lazyWithRetry(() => import("./features/admin/pages/GrowthFindingsPage"));
const AdminGrowthEngineSettingsPage = lazyWithRetry(() => import("./features/admin/pages/GrowthEngineSettingsPage"));
const AdminGrowthFindingDetailPage = lazyWithRetry(() => import("./features/admin/pages/GrowthFindingDetailPage"));
const AdminChangeLogPage = lazyWithRetry(() => import("./features/admin/pages/ChangeLogPage"));
const AdminContentLabPage = lazyWithRetry(() => import("./features/admin/pages/ContentLabPage"));
const AdminAgentPerformancePage = lazyWithRetry(() => import("./features/admin/pages/AgentPerformancePage"));
const AdminWhaleRegistryPage = lazyWithRetry(() => import("./features/admin/pages/WhaleRegistryPage"));
const BracketRoute = lazyWithRetry(() => import("./components/products/march-madness/bracket-route"));
const RunPoolRoute = lazyWithRetry(() => import("./components/products/run-pool/run-pool-route"));
const CoinStore = lazyWithRetry(() => import("./components/coins/CoinStore"));
const SendCoins = lazyWithRetry(() => import("./components/coins/SendCoins"));
const CoinShop = lazyWithRetry(() => import("./components/coins/CoinShop"));
const SuggestionWall = lazyWithRetry(() => import("./components/suggestion-wall/SuggestionWall"));
const WallOfLove = lazyWithRetry(() => import("./components/testimonials/WallOfLove"));
const SubmitTestimonial = lazyWithRetry(() => import("./components/testimonials/SubmitTestimonial"));
const AdminTestimonialsPage = lazyWithRetry(() => import("./features/admin/pages/TestimonialsPage"));
const AdminSupportInboxPage = lazyWithRetry(() => import("./features/admin/pages/SupportInboxPage"));
const MySupportRequestsPage = lazyWithRetry(() => import("./features/support/MySupportRequestsPage"));
const MiddlesRoute = lazyWithRetry(() => import("./components/products/middles/middles-route"));
const ParlayBuilderRoute = lazyWithRetry(() => import("./components/products/parlay-builder/parlay-builder-route"));
const ParlayBuilderShare = lazyWithRetry(() => import("./components/products/parlay-builder/parlay-builder-share"));
const BetAgentInstall = lazyWithRetry(() => import("./components/products/common/bet-agent/BetAgentInstall"));
const DeeplinkLab = lazyWithRetry(() => import("./components/products/common/book-deeplink/DeeplinkLab"));
const PricingPage = lazyWithRetry(() => import("./components/payments/pricing-page"));
const LivePage = lazyWithRetry(() => import("./components/live/live-page"));
const OddsJamComparisonPage = lazyWithRetry(
  () => import("./components/marketing/vs/oddsjam-comparison-page")
);

/* ─── Loading fallback for lazy routes ─── */
const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <div style={{ color: "#94c2dc", fontSize: "1rem", opacity: 0.7 }}>Loading…</div>
  </div>
);

const PricingRouteGate: React.FC<{
  authReady: boolean;
  children: React.ReactNode;
}> = ({ authReady, children }) => {
  // An explicit visit to /pricing always shows the pricing page — for
  // logged-out/free users (acquisition) AND for paid users (manage billing,
  // upgrade, downgrade). The pricing page renders its own CTAs per role, so
  // bouncing paid users to their product page here would lock them out of the
  // "Manage billing" flow and send admins to /admin. The redirect-to-product
  // behaviour lives on the acquisition routes (/signup, /subscribe) and the
  // generic CTAs that call getPricingDestination directly.
  if (!authReady) return <PageLoader />;
  return <>{children}</>;
};

const PricingRedirectGate: React.FC<{
  authReady: boolean;
  isAuthenticated: boolean;
  role?: User["role"] | string | null;
}> = ({ authReady, isAuthenticated, role }) => {
  if (!authReady) return <PageLoader />;

  return (
    <Navigate
      to={getPricingDestination({ isAuthenticated, role })}
      replace
    />
  );
};

/**
 * AuthGate – blocks the entire route tree from rendering until the initial
 * auth check has completed (authReady === true).  This prevents the "flash
 * of unauthenticated content" that happens when pages render before we know
 * whether the user is logged in.  Once authReady is true (success OR failure)
 * children render normally - unauthenticated users still see public pages.
 */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authReady } = useUser();

  if (!authReady) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#0b0f19",
        }}
      >
        <img
          src="https://storage.googleapis.com/assestsbookiebrawler/logos/BOOKIE%20BRAWLER%20(6).png"
          alt="BookieBrawler"
          width={300}
          height={72}
          style={{ height: 72, width: 300, marginBottom: 24, opacity: 0.85, objectFit: "contain" }}
        />
        <div
          style={{
            width: 36,
            height: 36,
            border: "3px solid rgba(148,194,220,0.15)",
            borderTopColor: "#94c2dc",
            borderRadius: "50%",
            animation: "spin .75s linear infinite",
          }}
        />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return <>{children}</>;
};

/** Scrolls to the top of the page on every route change */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/**
 * Fans a route change out to every analytics provider that needs a
 * manual pageview ping. PostHog has capture_pageview disabled in its
 * init; GA4's gtag config uses send_page_view:false. Both rely on
 * this component to record SPA navigations.
 */
type GrowthRouteMetadata = {
  route_template: string;
  page_category: string;
  surface: string;
  funnel_stage: string;
};

const getGrowthRouteMetadata = (pathname: string): GrowthRouteMetadata => {
  if (pathname === "/") {
    return {
      route_template: "/",
      page_category: "homepage",
      surface: "marketing",
      funnel_stage: "awareness",
    };
  }

  const contentPrefixes = [
    "/blog",
    "/betting-calculators",
    "/betting-education",
    "/online-sportsbooks",
    "/mlb",
    "/nba",
    "/casino",
    "/vs",
  ];

  if (contentPrefixes.some((prefix) => pathname.startsWith(prefix))) {
    return {
      route_template: contentPrefixes.find((prefix) => pathname.startsWith(prefix)) || pathname,
      page_category: "content",
      surface: "seo",
      funnel_stage: "education",
    };
  }

  const routeMap: Record<string, GrowthRouteMetadata> = {
    "/pricing": {
      route_template: "/pricing",
      page_category: "pricing",
      surface: "monetization",
      funnel_stage: "conversion",
    },
    "/start": {
      route_template: "/start",
      page_category: "quiz",
      surface: "funnel",
      funnel_stage: "conversion",
    },
    "/start/plan": {
      route_template: "/start/plan",
      page_category: "paywall",
      surface: "funnel",
      funnel_stage: "conversion",
    },
    "/signup": {
      route_template: "/signup",
      page_category: "pricing_redirect",
      surface: "monetization",
      funnel_stage: "conversion",
    },
    "/live": {
      route_template: "/live",
      page_category: "live",
      surface: "content",
      funnel_stage: "engagement",
    },
    "/positive-ev": {
      route_template: "/positive-ev",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "activation",
    },
    "/performance": {
      route_template: "/performance",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "engagement",
    },
    "/free-account": {
      route_template: "/free-account",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "activation",
    },
    "/my-bets": {
      route_template: "/my-bets",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "retention",
    },
    "/arbitrage": {
      route_template: "/arbitrage",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "activation",
    },
    "/sharp-money": {
      route_template: "/sharp-money",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "activation",
    },
    "/prediction-traders": {
      route_template: "/prediction-traders",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "activation",
    },
    "/middles": {
      route_template: "/middles",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "activation",
    },
    "/parlay-builder": {
      route_template: "/parlay-builder",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "activation",
    },
    "/sportsbook-promo-finder": {
      route_template: "/sportsbook-promo-finder",
      page_category: "product_tool",
      surface: "product",
      funnel_stage: "activation",
    },
    "/login": {
      route_template: "/login",
      page_category: "auth",
      surface: "account",
      funnel_stage: "signup",
    },
  };

  const routeMatch = Object.keys(routeMap)
    .sort((a, b) => b.length - a.length)
    .find((route) => pathname === route || pathname.startsWith(`${route}/`));

  if (routeMatch) return routeMap[routeMatch];

  if (pathname.startsWith("/admin")) {
    return {
      route_template: "/admin",
      page_category: "admin",
      surface: "admin",
      funnel_stage: "operations",
    };
  }

  if (pathname.startsWith("/account-settings") || pathname.startsWith("/profile")) {
    return {
      route_template: pathname.startsWith("/profile") ? "/profile/:profileId" : "/account-settings",
      page_category: "account",
      surface: "account",
      funnel_stage: "retention",
    };
  }

  return {
    route_template: pathname,
    page_category: "other",
    surface: "app",
    funnel_stage: "other",
  };
};

const getGrowthAttributionProperties = () => {
  const params = new URLSearchParams(window.location.search);
  const attribution: Record<string, string | boolean> = {};
  const campaignKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "gclid",
    "fbclid",
    "msclkid",
  ];

  campaignKeys.forEach((key) => {
    const value = params.get(key);
    if (value) attribution[key] = value;
  });

  attribution.referrer_domain = document.referrer
    ? (() => {
        try {
          return new URL(document.referrer).hostname;
        } catch {
          return "unknown";
        }
      })()
    : "direct";

  attribution.has_campaign_params = campaignKeys.some((key) => params.has(key));
  return attribution;
};

const PageviewTracker: React.FC = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const path = `${pathname}${search}`;
    const routeMetadata = getGrowthRouteMetadata(pathname);
    capturePageview(path);
    capture("growth_page_viewed", {
      path,
      ...routeMetadata,
      ...getGrowthAttributionProperties(),
    });
    captureGa4Pageview(path);

    const engagedTimer = window.setTimeout(() => {
      capture("growth_session_engaged", {
        path,
        engagement_seconds: 15,
        ...routeMetadata,
      });
    }, 15000);

    const reachedDepths = new Set<number>();
    const depthThresholds = [25, 50, 75, 90];
    const handleScroll = () => {
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;

      const depth = Math.round((window.scrollY / scrollableHeight) * 100);
      depthThresholds.forEach((threshold) => {
        if (depth < threshold || reachedDepths.has(threshold)) return;
        reachedDepths.add(threshold);
        capture("growth_scroll_depth_reached", {
          path,
          depth_percent: threshold,
          ...routeMetadata,
        });
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.clearTimeout(engagedTimer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [pathname, search]);

  return null;
};

/**
 * AppMain - wraps page content + footer and offsets it clear of the
 * AppNav shell. On desktop the rail occupies a fixed left gutter (CSS
 * margin); on mobile the top bar sits above and the bottom bar below (that
 * one is reserved from --bb-bottomnav-h, which BottomNav publishes, so the
 * gap always matches what's on screen). Immersive routes ship their own top
 * chrome, and the landing page keeps its full-bleed hero — both say so with
 * a modifier here.
 */
const AppMain: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const modifier = isChromelessRoute(pathname)
    ? " bb-app-main--chromeless"
    : pathname === "/"
      ? " bb-app-main--landing"
      : isImmersiveRoute(pathname)
        ? " bb-app-main--immersive"
        : "";
  return <div className={`bb-app-main${modifier}`}>{children}</div>;
};

/**
 * Renders its children only outside the chromeless funnel. `alsoOn` keeps a
 * piece on one funnel route, which is how One Tap reaches the paywall.
 */
const WithChrome: React.FC<{ alsoOn?: string; children: React.ReactNode }> = ({
  alsoOn,
  children,
}) => {
  const { pathname } = useLocation();
  return !isChromelessRoute(pathname) || pathname === alsoOn ? <>{children}</> : null;
};

function App() {
  const clientId = process.env.REACT_APP_BOOKIE_BRAWLER_GOOGLE_CLIENT_ID ?? "";

  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [currentUserId] = useState(0);
  const [usrState, setUsrState] = useState<User>();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const toggleModal = () => {
    setShowLoginModal(!showLoginModal);
  };

  // Let any component (e.g. the pricing-page tier CTAs) open the sign-in
  // modal in place via signInModalSignal instead of routing to /login. We
  // consume the request as an edge — reset the signal to false right after
  // opening — so a later CTA click re-triggers the effect and reopens it.
  const signInRequested = useSignalValue(signInModalSignal);
  useEffect(() => {
    if (!signInRequested) return;
    setShowLoginModal(true);
    signInModalSignal.value = false;
  }, [signInRequested]);

  const { user, setUser, authReady, setAuthReady } = useUser();

  const handleLogout = () => {
    // `logout()` is async and only removes the local tokens AFTER its
    // /auth/logout POST resolves. AppNav's sign-out reloads to "/" immediately
    // after calling this, so that reload was racing (and beating) the async
    // clear — leaving the user signed in. Clear the local tokens synchronously
    // here so the immediate navigation can't outrun it; the server-side POST
    // still fires best-effort.
    //
    // Push teardown is best-effort for the same reason: the reload may kill
    // it mid-flight, but deleteToken() invalidates the token at FCM itself,
    // so a row the backend didn't hear about is deactivated on its next send
    // (UNREGISTERED) — this browser stops receiving this account's pushes
    // either way. Must start before clearAuthTokens() so the API call can
    // still authenticate.
    void unregisterPushDevice();
    logout();
    clearAuthTokens();
    resetPostHog();
    resetGa4();
    resetClarity();
    setIsUserAuthenticated(false);
    setUser(defaultUser);
  };

  const handleLogin = async () => {
    setShowLoginModal(false);
    setIsUserAuthenticated(true);

    const checkoutResumed = await resumeCheckoutAfterLogin();
    if (checkoutResumed) return;

    // Check if we were redirected from a protected page (e.g. /account-settings)
    const returnTo = sessionStorage.getItem('returnTo');
    if (returnTo) {
      sessionStorage.removeItem('returnTo');
      window.location.href = returnTo;
      return;
    }

    if (sessionStorage.getItem('bb_new_account') === 'true') {
      sessionStorage.removeItem('bb_new_account');
      // A new account from the quiz has a plan open already, so it stays there.
      if (!isChromelessRoute(window.location.pathname)) {
        window.location.href = '/activation';
        return;
      }
    }

    // If user is already on a product/content page, stay there after login
    const stayOnPagePrefixes = [
      '/positive-ev',
      '/arbitrage',
      '/sharp-money',
      '/prediction-traders',
      '/13-run-pool',
      '/sportsbook-promo-finder',
      '/march-madness',
      '/betting-calculators',
      '/betting-education',
      '/blog',
      '/online-sportsbooks',
      '/mlb',
      '/nba',
      '/casino',
      '/suggestions',
      '/account-settings',
      '/admin',
      '/broadcast',
      '/contact-email',
      '/coins',
      '/profile',
      '/middles',
      '/parlay-builder',
      '/pricing',
      '/welcome',
      '/free-account',
      '/start',
    ];

    const currentPath = window.location.pathname;
    const shouldStayOnPage = stayOnPagePrefixes.some(prefix =>
      currentPath.startsWith(prefix)
    );

    if (shouldStayOnPage) {
      window.location.reload();
    } else {
      // "/" sends each member to the right home for their plan.
      window.location.href = "/";
    }
  };

  // useEffect(() => {
  //   const checkAuthentication = async () => {
  //     const authStatus = await getIsAuthenticated();
  //     // console.log("Auth status,: ", authStatus); // Added for debugging
  //    // setIsUserAuthenticated(authStatus);
  //     setAuthCheckFinished(true);
  //     if (authStatus) {
  //       const currentUser = await getCurrentUser();
  //       if (currentUser && currentUser.data) {
  //         const user = currentUser.data;
  //         setUser(user);
  //         setCurrentUserId(user.id);
  //         // setProfilePic(user.picture);
  //         // setUserRole(user.role);
  //       }
  //     }
  //     setIsInitialRender(false);
  //   };

  //   checkAuthentication();
  // }, []);

  // useEffect(() => {
  //   const updateUserContext = async () => {
  //     const response = await getCurrentUser();
  //     const currentUser = response?.data;
  //     const authed = await getIsAuthenticated();
  //     if (currentUser && authed) {
  //       setProfilePic(currentUser.picture);
  //       setUserRole(currentUser.role);
  //     }
  //   };

  //   updateUserContext();
  // }, []);

  useEffect(() => {
    const setUserState = async () => {
      try {
        // Run the user fetch and auth check in parallel rather than
        // sequentially — AuthGate blocks first paint until authReady,
        // so cutting this waterfall directly speeds up first render.
        const [response, authed] = await Promise.all([
          getCurrentUser(),
          getIsAuthenticated(),
        ]);
        const currentUser = response?.data;
        setUsrState(currentUser);
        // `authed` alone isn't enough — if the /users/me fetch itself came
        // back empty (transient error, race with a token refresh, etc.)
        // there's no user to populate the context with. Guard on
        // currentUser's presence too, or UserContext's `user` goes
        // `undefined` and every consumer that does `user.id` (e.g.
        // NotificationProvider) crashes on the next render.
        if (currentUser && currentUser.id !== 0 && authed) {
          setUser(currentUser);  // populate UserContext so notifications + other features work
          setProfilePic(currentUser.picture);
          setUserRole(currentUser.role);
          if (currentUser.email) setUserEmail(currentUser.email);
          // Account tier of the centralized state source of truth: the saved
          // residence_state wins over any browser/location value (and an empty
          // account adopts this device's pick). See promo-finder.tsx resolver.
          hydrateUserStateFromAccount(currentUser);
        }
      } catch (error) {
        console.error("[App] Failed to initialize user state:", error);
        // Auth is broken - clear stale tokens so the app can function unauthenticated
        clearAuthTokens();
        setUsrState(undefined);
      } finally {
        setAuthReady(true);
      }
    };

    setUserState();
  }, [setUser, setAuthReady]);

  // Bet-agent extension handshake — DORMANT BY DEFAULT.
  //
  // The web app now uses the no-extension deeplink path
  // (`SendToBookButton` + `buildBookDeeplink`) for "Add to slip", so
  // we no longer probe for the extension on every page load. The
  // extension code is still in-tree and the `/bet-agent/install`
  // diagnostics route still works; admins debugging the extension
  // can re-enable detection by flipping the localStorage flag:
  //
  //   localStorage.setItem("bb-bet-agent-debug", "1")
  //
  // This also keeps the handshake gated by admin role and
  // `authReady` (matching the previous race-fix behavior) so even
  // when the flag is on, non-admins don't trigger the bridge.
  useEffect(() => {
    if (!authReady) return;
    if (!currentUserHasBetAgentAccess()) return;
    let extensionDebug = false;
    try {
      extensionDebug =
        typeof localStorage !== "undefined" &&
        localStorage.getItem("bb-bet-agent-debug") === "1";
    } catch {
      /* localStorage unavailable in some private-mode contexts */
    }
    if (!extensionDebug) return;
    ensureBetAgentDetected();
  }, [authReady]);

  const authed = usrState ? true : false;

  return (
    <HelmetProvider>
        <NotificationProvider>
        <GoogleOAuthProvider clientId={clientId}>
          <Router>
            <AuthGate>
            <GlassDefs />
            <ScrollToTop />
            <PageviewTracker />
            <Helmet>
              <title>BookieBrawler: Positive EV & Arbitrage Betting Tools</title>
              <meta
                name="description"
                content="Find Positive EV bets, Arbitrage opportunities, and live odds edges with BookieBrawler's betting tools. Compare prices and act faster on value."
              />
              {/* Default OG tags - overridden by page-specific Helmets */}
              <meta property="og:title" content="BookieBrawler: Positive EV & Arbitrage Betting Tools" />
              <meta property="og:description" content="Find Positive EV bets, Arbitrage opportunities, and live odds edges with BookieBrawler's betting tools. Compare prices and act faster on value." />
              <meta property="og:image" content="https://storage.googleapis.com/assestsbookiebrawler/logos/share_bet.png" />
              <meta property="og:type" content="website" />
              <meta property="og:site_name" content="BookieBrawler" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content="BookieBrawler: Positive EV & Arbitrage Betting Tools" />
              <meta name="twitter:description" content="Find Positive EV bets, Arbitrage opportunities, and live odds edges with BookieBrawler's betting tools. Compare prices and act faster on value." />
              <meta name="twitter:image" content="https://storage.googleapis.com/assestsbookiebrawler/logos/share_bet.png" />
            </Helmet>

            {/* Drives --nav-bg-opacity 0→1 over the first 120px of scroll
                on the landing page so the top bar fades from transparent to
                solid navy (and stays solid everywhere else). */}
            <NavbarHandler setIsScrolled={() => {}} />

            {/* OddsJam-style app shell nav: a collapsible left rail on
                desktop, and a top bar + five-icon bottom bar + slide-in
                drawer on mobile. The admin pills, live pill, and auth
                CTA all live inside AppNav now. */}
            <WithChrome>
              <AppNav onLogout={handleLogout} toggleModal={toggleModal} />
            </WithChrome>

            {/* Support form for logged-in members. Opened from the Help icon
                in the nav (rail on desktop, bottom bar on mobile) or anywhere
                else via the support bus. */}
            <SupportWidget />
            {/* "Link copied · Post to feed →" toast for bet share buttons. */}
            <PostToFeedToast />
            {/* Auto-dismissing confirmations for board actions (Track etc). */}
            <BoardToast />
            {/* Floating EV parlay slip fed by the boards' Slip buttons. */}
            <WithChrome>
              <ParlaySlipDock />
            </WithChrome>
            {/* Dismissable launch-announcement card (registry-driven). */}
            <WithChrome>
              <AnnouncementHost />
            </WithChrome>
            {/* Asks members who predate a Terms version bump to agree. */}
            <TermsGate />
            <SignInModal
              show={showLoginModal}
              toggleModal={toggleModal}
              onLogin={handleLogin}
            />

            {/* Google One Tap - lives outside the modal so it's never
                blocked by a backdrop.  Waits for authReady before activating;
                stays disabled when the user is already signed in. */}
            <WithChrome alsoOn="/start/plan">
              <GoogleOneTap onLogin={handleLogin} />
            </WithChrome>

            <AppMain>
              <div className="page-content">
                <BillingStrip />
                <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* <Route
            path="/"
            element={
              <OnlySignIn
                onLogin={setIsUserAuthenticated}
                onLogout={handleLogout}
                isAuthenticated={isUserAuthenticated}
              />
            }
          /> */}
                  {/* <Route
                path="/home"
                element={
                  <BetFinder isUserAuthenticated={isUserAuthenticated} />
                }
              /> */}
                  {/* <Route
                path="/bets"
                element={
                  <AccessControl
                    component={SureBetSection}
                    name="SureBetSection"
                  />
                }
              /> */}
                  {/* <Route 
                path="/parlays"
                element={
                  <AccessControl
                    component={ParlayBuilder}
                    name="ParlayBuilder"
                  />
                }
              /> */}
                  {/* <Route
                path="/juicer"
                element={
                  <AccessControl component={BetJuicer} name="BetJuicer" />
                }
              /> */}
                  <Route
                    path="/pricing"
                    element={
                      <PricingRouteGate authReady={authReady}>
                        <PricingPage />
                      </PricingRouteGate>
                    }
                  />
                  {/* Acquisition URLs stay as redirects into the canonical pricing route
                      for logged-out/free users; paid users go to their owned destination. */}
                  <Route
                    path="/signup"
                    element={
                      <PricingRedirectGate
                        authReady={authReady}
                        isAuthenticated={authed}
                        role={user?.role}
                      />
                    }
                  />
                  <Route
                    path="/subscribe"
                    element={
                      <PricingRedirectGate
                        authReady={authReady}
                        isAuthenticated={authed}
                        role={user?.role}
                      />
                    }
                  />

                  {/* Daily live show on bookiebrawler.com — uses the
                      permanent YouTube channel-based embed so we never
                      have to swap a video ID per broadcast. See
                      live-page.tsx for the full embed strategy. */}
                  <Route path="/live" element={<LivePage />} />
                  <Route path="/glass-lab" element={<GlassLab />} />
                  <Route path="/exit-survey" element={<ExitSurveyPage />} />
                  <Route path="/come-back" element={<ComeBackPage />} />
                  <Route path="/reactivate" element={<ReactivatePage />} />
                  <Route path="/customer-call" element={<CustomerCallPage />} />
                  <Route path="/watch" element={<Navigate to="/live" replace />} />
                  <Route path="/stream" element={<Navigate to="/live" replace />} />
                  <Route
                    path="/account-settings/*"
                    element={<AccountSettings />}
                  />
                  <Route
                    path="/profile/:profileId"
                    element={<PublicProfile />}
                  />
                  <Route
                    path="/login"
                    element={
                      <Login
                        onLogin={setIsUserAuthenticated}
                        isAuthenticated={isUserAuthenticated}
                        onLogout={handleLogout}
                        redirectedFrom={""}
                        toggleModal={toggleModal}
                      />
                    }
                  />
                  <Route path="/" element={<HomeRoute />} />
                  <Route
                    path="/positive-ev"
                    element={<PositiveEVRoute authed={authed} />}
                  />
                  <Route path="/performance" element={<PerformanceRoute />} />
                  <Route path="/performance/:track" element={<PerformanceRoute />} />
                  <Route path="/my-bets" element={<MyBetsRoute authed={authed} />} />

                  <Route
                    path="/positive-ev/tips"
                    element={
                      <div className="p-5">
                        <EVTip/>
                      </div>
                    }
                  />
                  <Route
                    path="/positive-ev/share/:shareId"
                    element={<PositiveEVShare />}
                  />
                  <Route
                    path="/ev-welcome"
                    element={<NewSubscriber authed={authed} />}
                  />
                  {/* New tier-aware post-checkout landing. /ev-welcome is
                      kept as an alias for any outbound emails / Stripe
                      success URLs still pointing at the old path. */}
                  <Route
                    path="/welcome"
                    element={<NewSubscriber authed={authed} />}
                  />
                  <Route
                    path="/arbitrage"
                    element={<ArbitrageRoute authed={authed} />}
                  />
                  <Route
                    path="/sharp-money"
                    element={<SharpMoneyRoute authed={authed} />}
                  />
                  <Route
                    path="/sharp-money/share/:shareId"
                    element={<SharpMoneyShare />}
                  />
                  <Route
                    path="/prediction-traders"
                    element={<PredictionTradersRoute authed={authed} />}
                  />
                  <Route
                    path="/prediction-traders/share/:shareId"
                    element={<PredictionTradersShare />}
                  />
                  <Route
                    path="/arbitrage/share/:shareId"
                    element={<ArbitrageShare />}
                  />
                  <Route
                    path="/parlay-builder"
                    element={<ParlayBuilderRoute authed={authed} />}
                  />
                  <Route
                    path="/parlay-builder/share/:shareId"
                    element={<ParlayBuilderShare />}
                  />
                  <Route
                    path="/sportsbook-promo-finder"
                    element={<AllPromos />}
                  />

                  {/* <Route
                path="/account-settings/overview"
                element={<Overview initialUser={ usrState }/>}
              /> */}
                  {/* <Route
                nd"/account-settings/customer-portal"
                element={<CustomerPortal />}
              /> */}
                  {/* <Route
                    path="/admin/utils"
                    element={<AccessControl component={Util} name="Util" />}
                  /> */}

                  <Route path="/blog" element={<BrawlerBlog />} />
                  <Route
                    path="/blog/:slug"
                    element={<ContentPostView contentType="Blog" />}
                  />
                  <Route
                    path="/betting-calculators"
                    element={<BettingCalculators />}
                  />
                  <Route
                    path="/betting-calculators/:slug"
                    element={<CalculatorContent />}
                  />
                  <Route
                    path="/betting-education"
                    element={<BettingEducation />}
                  />
                  <Route
                    path="/betting-education/:slug"
                    element={
                      <ContentPostView contentType="Betting Education" />
                    }
                  />
                  <Route path="/vs/oddsjam" element={<OddsJamComparisonPage />} />
                  <Route
                    path="/online-sportsbooks/:slug"
                    element={
                      <ContentPostView contentType="Online Sportsbooks" />
                    }
                  />

                  <Route
                    path="/mlb/:slug"
                    element={<ContentPostView contentType="MLB Blog" />}
                  />

                  <Route
                    path="/nba/:slug"
                    element={<ContentPostView contentType="NBA Blog" />}
                  />

                  <Route
                    path="/casino/:slug"
                    element={<ContentPostView contentType="Casino Blog" />}
                  />

                  <Route
                    path="/unsubscribed-successfully"
                    element={<Navigate to="/email/unsubscribed" replace />}
                  />
                  <Route path="/email/unsubscribed" element={<EmailUnsubscribedPage />} />

                  <Route
                    path="/getting-started"
                    element={
                      <Navigate
                        to="/betting-education/getting-started-with-bookiebrawler"
                        replace
                      />
                    }
                  />

                  <Route
                    path="/march-madness-2026"
                    element={<BracketRoute />}
                  />

                  {/* 301 SEO redirect: old /run-pool → new /13-run-pool */}
                  <Route
                    path="/run-pool/*"
                    element={<Navigate to="/13-run-pool" replace />}
                  />

                  <Route
                    path="/13-run-pool/*"
                    element={<RunPoolRoute />}
                  />

                  <Route
                    path="/coins"
                    element={<CoinStore />}
                  />

                  <Route
                    path="/coins/send"
                    element={<SendCoins />}
                  />

                  <Route
                    path="/coins/shop"
                    element={<CoinShop />}
                  />

                  <Route
                    path="/broadcast"
                    element={<BroadcastPage />}
                  />

                  <Route
                    path="/contact-email"
                    element={<ContactEmailPage />}
                  />

                  {/* Admin dashboard. AdminLayout itself enforces the
                      ADMIN role gate and renders an access-denied
                      panel for everyone else. The same role check is
                      enforced server-side on /api/v1/admin/dashboard/**. */}
                  <Route path="/activation" element={<ActivationPage />} />
                  <Route path="/estimate" element={<ProfitEstimatorPage />} />
                  <Route path="/odds" element={<OddsPage />} />
                  <Route path="/odds/:league" element={<OddsPage />} />
                  <Route path="/odds/:league/game/:id" element={<OddsGamePage />} />
                  <Route path="/odds/:league/:team" element={<OddsPage />} />
                  <Route path="/free-account" element={<FreeAccountPage />} />
                  <Route path="/start" element={<StartPage />} />
                  <Route path="/start/plan" element={<PlanPage />} />
                  {/* Email magic-link landing (/auth/email?token=...). */}
                  <Route path="/auth/email" element={<EmailLinkVerify />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminOverviewPage />} />
                    <Route path="relationships" element={<RelationshipsPage />} />
                    <Route path="insights" element={<AdminInsightsPage />} />
                    <Route path="analytics" element={<AdminAnalyticsPage />} />
                    <Route path="performance" element={<AdminAgentPerformancePage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="leads" element={<AdminLeadsPage />} />
                    <Route path="users/recent" element={<AdminRecentSignupsPage />} />
                    <Route path="users/:id" element={<AdminUserDetailPage />} />
                    <Route path="segments" element={<AdminSegmentsPage />} />
                    <Route path="email-settings" element={<AdminEmailSettingsPage />} />
                    <Route path="campaigns" element={<AdminCampaignsListPage />} />
                    <Route path="campaigns/new" element={<AdminCampaignBuilderPage />} />
                    <Route path="campaigns/:id/edit" element={<AdminCampaignBuilderPage />} />
                    <Route path="campaigns/:id" element={<AdminCampaignDetailPage />} />
                    <Route path="content" element={<AdminContentLabPage />} />
                    <Route path="whales" element={<AdminWhaleRegistryPage />} />
                    <Route path="growth" element={<AdminGrowthFindingsPage />} />
                    <Route path="growth/engine" element={<AdminGrowthEngineSettingsPage />} />
                    <Route path="growth/changelog" element={<AdminChangeLogPage />} />
                    <Route path="growth/:id" element={<AdminGrowthFindingDetailPage />} />
                    <Route path="testimonials" element={<AdminTestimonialsPage />} />
                    <Route path="support" element={<AdminSupportInboxPage />} />
                  </Route>

                  <Route
                    path="/suggestions"
                    element={<SuggestionWall userId={currentUserId} />}
                  />

                  <Route path="/feed" element={<FeedPage />} />
                  <Route path="/feed/post/:id" element={<PostThreadPage />} />

                  <Route path="/communities" element={<CommunitiesPage />} />
                  <Route path="/communities/new" element={<CreateCommunityPage />} />
                  <Route path="/communities/:id" element={<CommunityPage />} />
                  <Route path="/communities/:id/edit" element={<EditCommunityPage />} />
                  <Route path="/communities/:id/members" element={<CommunityMembersPage />} />

                  {/* Legal. /terms-of-service and /privacy-policy are the
                      URLs already published in structured data and older
                      emails, so they redirect rather than 404. */}
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route
                    path="/terms-of-service"
                    element={<Navigate to="/terms" replace />}
                  />
                  <Route
                    path="/privacy-policy"
                    element={<Navigate to="/privacy" replace />}
                  />

                  <Route path="/testimonials" element={<WallOfLove />} />
                  <Route path="/support" element={<MySupportRequestsPage />} />
                  <Route
                    path="/wall-of-love"
                    element={<Navigate to="/testimonials" replace />}
                  />
                  <Route path="/testimonials/submit" element={<SubmitTestimonial />} />
                  <Route
                    path="/testimonials/submit/:token"
                    element={<SubmitTestimonial />}
                  />

                  <Route
                    path="/middles"
                    element={<MiddlesRoute authed={authed} />}
                  />

                  {/* Admin-only Bet Agent install page. The component
                      itself enforces the role check via hasAccess and
                      redirects non-admins to /login. */}
                  <Route
                    path="/bet-agent/install"
                    element={<BetAgentInstall />}
                  />

                  {/* Admin-only Deeplink Lab — live preview /
                      iteration loop for the no-extension "Add to slip"
                      URL builder. See DeeplinkLab.tsx for the full
                      verification flow. */}
                  <Route
                    path="/bet-agent/deeplink-lab"
                    element={<DeeplinkLab />}
                  />

                  {/* Catch-all - must be LAST */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
              </div>
              <WithChrome>
                <div className="bg-gradient-main bb-app-footer">
                  <Suspense fallback={null}>
                    <Directory />
                  </Suspense>
                  <Disclaimer />
                </div>
              </WithChrome>
            </AppMain>
          </AuthGate>
          <AgentStatusDrawer />
          </Router>
        </GoogleOAuthProvider>
        </NotificationProvider>
    </HelmetProvider>
  );
}

export default App;
